/**
 * DockingDownloadResults.jsx
 *
 * Props:
 *   poseFiles   {Array<{name: string, text: string}>}
 *   ixnImages   {Array<{name: string, url: string}>}
 *   scores      {Array}
 *   jobId       {string}
 *   onClose     {Function}
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  X, Download, FileText, ImageIcon, ChevronDown, ChevronUp,
  CheckCircle2, Circle, Maximize2, ChevronLeft, ChevronRight,
  Package, Dna, Microscope,
} from "lucide-react";

/* ─── helpers ─────────────────────────────────────────────── */
function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function saveTextAsFile(text, filename) {
  saveBlob(new Blob([text], { type: "chemical/x-pdb" }), filename);
}

async function urlToBlob(url) {
  const res = await fetch(url);
  return res.blob();
}

/* ─── AffinityBadge ───────────────────────────────────────── */
function AffinityBadge({ value }) {
  if (value === null || value === undefined) return null;
  const v = Number(value);
  const bg = v <= -9 ? "#4c1d95" : v <= -7 ? "#7c3aed" : v <= -5 ? "#d97706" : "#6b7280";
  return (
    <span style={{
      fontFamily: "monospace", fontSize: 10, fontWeight: 800,
      padding: "2px 7px", borderRadius: 20, background: bg, color: "#fff",
    }}>
      {v.toFixed(3)} kcal/mol
    </span>
  );
}

/* ─── PdbRow ──────────────────────────────────────────────── */
function PdbRow({ pf, index, score, selected, onToggle, onPreview, previewing }) {
  const name = pf.name.replace(/\.[^.]+$/, "");
  const affinity = score?.affinity ?? score?.minimizedAffinity ?? null;

  return (
    <div>
      <div
        onClick={() => onToggle(index)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", borderRadius: 8, marginBottom: previewing ? 0 : 6,
          border: `1.5px solid ${selected ? "#7c3aed" : "#e5e7eb"}`,
          background: selected ? "#f5f3ff" : "#fff",
          cursor: "pointer", transition: "all 0.15s",
          borderBottomLeftRadius: previewing ? 0 : 8,
          borderBottomRightRadius: previewing ? 0 : 8,
        }}
        onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = "#c4b5fd"; }}
        onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = selected ? "#7c3aed" : "#e5e7eb"; }}
      >
        {/* custom checkbox */}
        <div onClick={e => { e.stopPropagation(); onToggle(index); }}
          style={{ flexShrink: 0, color: selected ? "#7c3aed" : "#d1d5db", cursor: "pointer" }}>
          {selected
            ? <CheckCircle2 size={18} strokeWidth={2.5} />
            : <Circle size={18} strokeWidth={1.5} />}
        </div>

        {/* pose badge */}
        <span style={{
          width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
          background: selected ? "#7c3aed" : "#f3f4f6",
          color: selected ? "#fff" : "#6b7280",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 800,
        }}>
          {index + 1}
        </span>

        {/* name + affinity */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 700,
            color: selected ? "#5b21b6" : "#374151",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            marginBottom: 2,
          }}>
            {name}
          </div>
          <AffinityBadge value={affinity} />
        </div>

        {/* preview toggle */}
        <button
          onClick={e => { e.stopPropagation(); onPreview(index); }}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "4px 8px", borderRadius: 6, flexShrink: 0,
            border: `1px solid ${previewing ? "#7c3aed" : "#e5e7eb"}`,
            background: previewing ? "#ede9fe" : "#fff",
            color: previewing ? "#7c3aed" : "#9ca3af",
            fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}
        >
          {previewing ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {previewing ? "Hide" : "Preview"}
        </button>
      </div>

      {/* inline preview */}
      {previewing && (
        <div style={{
          marginBottom: 6,
          border: "1.5px solid #7c3aed", borderTop: "none",
          borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
          background: "#faf8ff", overflow: "hidden",
        }}>
          <div style={{ display: "flex", gap: 14, padding: "6px 12px", background: "#f5f3ff", borderBottom: "1px solid #ede9fe", flexWrap: "wrap" }}>
            {[
              ["Lines",  pf.text.split("\n").length.toLocaleString()],
              ["ATOM",   (pf.text.match(/^ATOM/gm) || []).length],
              ["HETATM", (pf.text.match(/^HETATM/gm) || []).length],
              ["Size",   (new Blob([pf.text]).size / 1024).toFixed(1) + " KB"],
            ].map(([l, v]) => (
              <div key={l}>
                <div style={{ fontSize: 9, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>{l}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#5b21b6", fontFamily: "monospace" }}>{v}</div>
              </div>
            ))}
          </div>
          <pre style={{
            margin: 0, padding: "8px 12px",
            fontFamily: "monospace", fontSize: 10, color: "#374151",
            lineHeight: 1.6, maxHeight: 160, overflowY: "auto", overflowX: "auto", whiteSpace: "pre",
          }}>
            {pf.text.split("\n").slice(0, 50).join("\n")}
            {pf.text.split("\n").length > 50 && "\n… (truncated)"}
          </pre>
          <div style={{ padding: "6px 12px", borderTop: "1px solid #ede9fe" }}>
            <button
              onClick={() => saveTextAsFile(pf.text, pf.name.endsWith(".pdb") ? pf.name : pf.name + ".pdb")}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "5px 12px", borderRadius: 7,
                border: "1px solid #ddd6fe", background: "#fff",
                color: "#7c3aed", fontSize: 11, fontWeight: 700, cursor: "pointer",
              }}
            >
              <Download size={12} /> Download this file only
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ImageCard ───────────────────────────────────────────── */
function ImageCard({ img, index, selected, onToggle, onFullscreen }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onToggle(index)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative", borderRadius: 10, overflow: "hidden", cursor: "pointer",
        border: `2px solid ${selected ? "#7c3aed" : hovered ? "#c4b5fd" : "#e5e7eb"}`,
        transition: "all 0.15s",
      }}
    >
      {/* selection badge */}
      <div style={{
        position: "absolute", top: 6, left: 6, zIndex: 2,
        color: selected ? "#7c3aed" : "rgba(255,255,255,0.9)",
        filter: selected ? "none" : "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
      }}>
        {selected ? <CheckCircle2 size={18} fill="#fff" strokeWidth={2.5} /> : <Circle size={18} strokeWidth={1.5} />}
      </div>

      {/* fullscreen btn on hover */}
      {hovered && !selected && (
        <button
          onClick={e => { e.stopPropagation(); onFullscreen(index); }}
          style={{
            position: "absolute", top: 6, right: 6, zIndex: 2,
            padding: "4px 6px", borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.5)",
            background: "rgba(0,0,0,0.4)", color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center",
          }}
        >
          <Maximize2 size={12} />
        </button>
      )}

      <img src={img.url} alt={img.name}
        style={{ width: "100%", display: "block", aspectRatio: "1/1", objectFit: "contain", background: "#f9fafb" }} />

      <div style={{
        padding: "5px 8px", fontSize: 10, fontWeight: 600, color: "#6b7280",
        background: selected ? "#f5f3ff" : "#fafafa",
        borderTop: `1px solid ${selected ? "#ede9fe" : "#f0f0f5"}`,
        textAlign: "center",
      }}>
        Pose {index + 1}
      </div>
    </div>
  );
}

/* ─── main component ──────────────────────────────────────── */
export default function DockingDownloadResults({
  poseFiles  = [],
  ixnImages  = [],
  scores     = [],
  jobId      = "",
  onClose,
}) {
  const [tab,           setTab]           = useState("pdb");
  // Start with nothing selected
  const [selPdb,        setSelPdb]        = useState(new Set());
  const [selImg,        setSelImg]        = useState(new Set());
  const [previewIdx,    setPreviewIdx]    = useState(null);
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const [downloading,   setDownloading]   = useState(false);
  const [dlProgress,    setDlProgress]    = useState("");
  const backdropRef = useRef(null);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const togglePdb = useCallback(i => {
    setSelPdb(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }, []);

  const toggleImg = useCallback(i => {
    setSelImg(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }, []);

  const allPdbSelected = poseFiles.length > 0 && selPdb.size === poseFiles.length;
  const allImgSelected = ixnImages.length > 0 && selImg.size === ixnImages.length;

  /* ── download helpers ── */
  async function downloadSelectedPdb() {
    const indices = [...selPdb].sort((a, b) => a - b);
    if (!indices.length) return;
    setDownloading(true);
    try {
      if (indices.length === 1) {
        setDlProgress("Saving PDB…");
        const pf = poseFiles[indices[0]];
        saveTextAsFile(pf.text, pf.name.endsWith(".pdb") ? pf.name : pf.name + ".pdb");
      } else {
        setDlProgress(`Packing ${indices.length} PDB files…`);
        const JSZipImport = await import("jszip");
        const JSZip = JSZipImport.default || JSZipImport;
        const zip = new JSZip();
        for (const i of indices) {
          const pf = poseFiles[i];
          zip.file(pf.name.endsWith(".pdb") ? pf.name : pf.name + ".pdb", pf.text);
        }
        const blob = await zip.generateAsync({ type: "blob" });
        saveBlob(blob, `${jobId || "docking"}-poses.zip`);
      }
    } finally { setDownloading(false); setDlProgress(""); }
  }

  async function downloadSingleImg(i) {
    const img = ixnImages[i];
    if (!img) return;
    const blob = await urlToBlob(img.url);
    saveBlob(blob, img.name.endsWith(".png") ? img.name : img.name + ".png");
  }

  async function downloadSelectedImg() {
    const indices = [...selImg].sort((a, b) => a - b);
    if (!indices.length) return;
    setDownloading(true);
    try {
      if (indices.length === 1) {
        setDlProgress("Saving image…");
        await downloadSingleImg(indices[0]);
      } else {
        setDlProgress(`Packing ${indices.length} images…`);
        const JSZipImport = await import("jszip");
        const JSZip = JSZipImport.default || JSZipImport;
        const zip = new JSZip();
        for (const i of indices) {
          setDlProgress(`Adding image ${i + 1}/${indices.length}…`);
          const blob = await urlToBlob(ixnImages[i].url);
          zip.file(ixnImages[i].name.endsWith(".png") ? ixnImages[i].name : ixnImages[i].name + ".png", blob);
        }
        const blob = await zip.generateAsync({ type: "blob" });
        saveBlob(blob, `${jobId || "docking"}-interactions.zip`);
      }
    } finally { setDownloading(false); setDlProgress(""); }
  }

  /* "Download all selected" — only adds folders that have selections */
  async function downloadAll() {
    const pdbIndices = [...selPdb].sort((a, b) => a - b);
    const imgIndices = [...selImg].sort((a, b) => a - b);
    if (!pdbIndices.length && !imgIndices.length) return;

    // If only one type is selected, delegate to the simpler handler
    if (!imgIndices.length) { await downloadSelectedPdb(); return; }
    if (!pdbIndices.length) { await downloadSelectedImg(); return; }

    setDownloading(true);
    try {
      const JSZipImport = await import("jszip");
      const JSZip = JSZipImport.default || JSZipImport;
      const zip = new JSZip();

      // Only create poses/ folder if pdbs are selected
      if (pdbIndices.length) {
        const pdbFolder = zip.folder("poses");
        for (const i of pdbIndices) {
          const pf = poseFiles[i];
          pdbFolder.file(pf.name.endsWith(".pdb") ? pf.name : pf.name + ".pdb", pf.text);
        }
      }

      // Only create interactions/ folder if images are selected
      if (imgIndices.length) {
        const imgFolder = zip.folder("interactions");
        for (const i of imgIndices) {
          setDlProgress(`Adding image ${i + 1}/${imgIndices.length}…`);
          const blob = await urlToBlob(ixnImages[i].url);
          imgFolder.file(ixnImages[i].name.endsWith(".png") ? ixnImages[i].name : ixnImages[i].name + ".png", blob);
        }
      }

      setDlProgress("Generating ZIP…");
      const blob = await zip.generateAsync({ type: "blob" });
      saveBlob(blob, `${jobId || "docking"}-results.zip`);
    } finally { setDownloading(false); setDlProgress(""); onClose(); }
  }

  const totalSelected = selPdb.size + selImg.size;

  const tabStyle = active => ({
    flex: 1, padding: "10px 8px",
    fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.06em",
    background: active ? "#fff" : "transparent",
    color: active ? "#7c3aed" : "#9ca3af",
    border: "none",
    borderBottom: active ? "2px solid #7c3aed" : "2px solid transparent",
    cursor: "pointer", transition: "all 0.2s",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  });

  const countBadge = (n, active) => n > 0 ? (
    <span style={{
      background: active ? "#ede9fe" : "#f3f4f6",
      color: active ? "#7c3aed" : "#9ca3af",
      borderRadius: 10, padding: "1px 6px",
      fontSize: 10, fontWeight: 800,
    }}>{n}</span>
  ) : null;

  return (
    <>
      {/* backdrop */}
      <div
        ref={backdropRef}
        onClick={e => { if (e.target === backdropRef.current) onClose?.(); }}
        style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(15,10,30,0.65)",
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(3px)", padding: 16,
        }}
      >
        {/* modal — smaller: 520px wide, auto height capped at 78vh */}
        <div style={{
          width: "100%", maxWidth: 520,
          maxHeight: "78vh",
          background: "#fff", borderRadius: 16,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(124,58,237,0.1)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          animation: "dlIn 0.2s cubic-bezier(0.34,1.56,0.64,1)",
        }}>
          <style>{`
            @keyframes dlIn { from{opacity:0;transform:scale(0.95) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
            .dl-scroll::-webkit-scrollbar{width:4px}
            .dl-scroll::-webkit-scrollbar-thumb{background:#ede9fe;border-radius:10px}
            .dl-img-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:10px}
          `}</style>

          {/* ── header ── */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "14px 18px 0", flexShrink: 0,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 3px 10px rgba(124,58,237,0.3)",
            }}>
              <Package size={17} color="#fff" strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>Download Results</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                {jobId ? `Job ${jobId.slice(0, 14)}…` : "Select files to download"}
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
              border: "1px solid #e5e7eb", background: "#f9fafb",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              color: "#9ca3af", transition: "all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#374151"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.color = "#9ca3af"; }}
            >
              <X size={14} />
            </button>
          </div>

          {/* ── tabs ── */}
          <div style={{ display: "flex", borderBottom: "1px solid #f0f0f5", marginTop: 12, flexShrink: 0 }}>
            <button style={tabStyle(tab === "pdb")} onClick={() => setTab("pdb")}>
              <Dna size={13} /> PDB Files {countBadge(poseFiles.length, tab === "pdb")}
            </button>
            <button style={tabStyle(tab === "images")} onClick={() => setTab("images")}>
              <Microscope size={13} /> Interactions {countBadge(ixnImages.length, tab === "images")}
            </button>
          </div>

          {/* ══ PDB TAB ══ */}
          {tab === "pdb" && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {poseFiles.length === 0 ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#d1d5db", padding: 32, textAlign: "center" }}>
                  <FileText size={38} strokeWidth={1} style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 13, fontWeight: 700 }}>No PDB poses available</div>
                </div>
              ) : (
                <>
                  {/* select bar */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderBottom: "1px solid #f9fafb", flexShrink: 0 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>
                      <input type="checkbox"
                        style={{ accentColor: "#7c3aed", width: 14, height: 14, cursor: "pointer" }}
                        checked={allPdbSelected}
                        onChange={e => e.target.checked
                          ? setSelPdb(new Set(poseFiles.map((_, i) => i)))
                          : setSelPdb(new Set())}
                      />
                      Select all
                    </label>
                    {selPdb.size > 0 && (
                      <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 700, background: "#f5f3ff", padding: "2px 8px", borderRadius: 10 }}>
                        {selPdb.size} of {poseFiles.length}
                      </span>
                    )}
                    {selPdb.size > 0 && (
                      <button onClick={() => setSelPdb(new Set())}
                        style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                        Clear
                      </button>
                    )}
                  </div>

                  {/* list */}
                  <div className="dl-scroll" style={{ flex: 1, overflowY: "auto", padding: "10px 16px" }}>
                    {poseFiles.map((pf, i) => (
                      <PdbRow key={pf.name}
                        pf={pf} index={i} score={scores[i]}
                        selected={selPdb.has(i)}
                        onToggle={togglePdb}
                        onPreview={idx => setPreviewIdx(previewIdx === idx ? null : idx)}
                        previewing={previewIdx === i}
                      />
                    ))}
                  </div>

                  {/* footer */}
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #f0f0f5", flexShrink: 0 }}>
                    <button onClick={downloadSelectedPdb} disabled={selPdb.size === 0 || downloading}
                      style={{
                        width: "100%", padding: "9px 12px", borderRadius: 9, border: "none",
                        background: selPdb.size === 0 || downloading ? "#c4b5fd"
                          : "linear-gradient(135deg,#7c3aed,#6d28d9)",
                        color: "#fff", fontSize: 12, fontWeight: 800,
                        cursor: selPdb.size === 0 || downloading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                        boxShadow: selPdb.size > 0 && !downloading ? "0 3px 10px rgba(124,58,237,0.3)" : "none",
                        transition: "all 0.2s",
                      }}>
                      <Download size={14} />
                      {downloading ? dlProgress || "Preparing…"
                        : selPdb.size === 0 ? "Select poses to download"
                        : selPdb.size === 1 ? `Download ${poseFiles[[...selPdb][0]]?.name}`
                        : `Download ${selPdb.size} poses as ZIP`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ IMAGES TAB ══ */}
          {tab === "images" && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {ixnImages.length === 0 ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#d1d5db", padding: 32, textAlign: "center" }}>
                  <ImageIcon size={38} strokeWidth={1} style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 13, fontWeight: 700 }}>No interaction images available</div>
                </div>
              ) : (
                <>
                  {/* select bar */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderBottom: "1px solid #f9fafb", flexShrink: 0 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>
                      <input type="checkbox"
                        style={{ accentColor: "#7c3aed", width: 14, height: 14, cursor: "pointer" }}
                        checked={allImgSelected}
                        onChange={e => e.target.checked
                          ? setSelImg(new Set(ixnImages.map((_, i) => i)))
                          : setSelImg(new Set())}
                      />
                      Select all
                    </label>
                    {selImg.size > 0 && (
                      <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 700, background: "#f5f3ff", padding: "2px 8px", borderRadius: 10 }}>
                        {selImg.size} of {ixnImages.length}
                      </span>
                    )}
                    {selImg.size > 0 && (
                      <button onClick={() => setSelImg(new Set())}
                        style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                        Clear
                      </button>
                    )}
                  </div>

                  {/* grid */}
                  <div className="dl-scroll" style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                    <div className="dl-img-grid">
                      {ixnImages.map((img, i) => (
                        <ImageCard key={img.name} img={img} index={i}
                          selected={selImg.has(i)}
                          onToggle={toggleImg}
                          onFullscreen={setFullscreenIdx}
                        />
                      ))}
                    </div>
                  </div>

                  {/* footer */}
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #f0f0f5", flexShrink: 0 }}>
                    <button onClick={downloadSelectedImg} disabled={selImg.size === 0 || downloading}
                      style={{
                        width: "100%", padding: "9px 12px", borderRadius: 9, border: "none",
                        background: selImg.size === 0 || downloading ? "#a7f3d0"
                          : "linear-gradient(135deg,#059669,#047857)",
                        color: "#fff", fontSize: 12, fontWeight: 800,
                        cursor: selImg.size === 0 || downloading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                        boxShadow: selImg.size > 0 && !downloading ? "0 3px 10px rgba(5,150,105,0.3)" : "none",
                        transition: "all 0.2s",
                      }}>
                      <Download size={14} />
                      {downloading ? dlProgress || "Preparing…"
                        : selImg.size === 0 ? "Select images to download"
                        : selImg.size === 1 ? `Download ${ixnImages[[...selImg][0]]?.name}`
                        : `Download ${selImg.size} images as ZIP`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── bottom bar: cross-tab download all selected ── */}
          {(poseFiles.length > 0 || ixnImages.length > 0) && (
            <div style={{
              padding: "10px 16px 14px", borderTop: "2px solid #f5f3ff", flexShrink: 0,
              background: "linear-gradient(180deg,#fff 0%,#faf8ff 100%)",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            }}>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {totalSelected > 0 ? (
                  <>
                    <span style={{ color: "#7c3aed", fontWeight: 700 }}>{totalSelected} file{totalSelected !== 1 ? "s" : ""}</span>
                    {selPdb.size > 0 && <span> · {selPdb.size} PDB</span>}
                    {selImg.size > 0 && <span> · {selImg.size} image{selImg.size !== 1 ? "s" : ""}</span>}
                  </>
                ) : (
                  <span style={{ color: "#d1d5db" }}>Nothing selected</span>
                )}
              </div>
              <button onClick={downloadAll} disabled={totalSelected === 0 || downloading}
                style={{
                  padding: "8px 16px", borderRadius: 9, border: "none", whiteSpace: "nowrap",
                  background: totalSelected === 0 || downloading ? "#e5e7eb"
                    : "linear-gradient(135deg,#1e1b4b,#3730a3)",
                  color: totalSelected === 0 ? "#9ca3af" : "#fff",
                  fontSize: 12, fontWeight: 800,
                  cursor: totalSelected === 0 || downloading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  boxShadow: totalSelected > 0 && !downloading ? "0 3px 10px rgba(30,27,75,0.3)" : "none",
                  transition: "all 0.2s",
                }}>
                <Download size={13} />
                {downloading ? dlProgress || "Preparing…" : "Download all selected"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── fullscreen image ── */}
      {fullscreenIdx !== null && ixnImages[fullscreenIdx] && (
        <div onClick={() => setFullscreenIdx(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)",
            zIndex: 9999, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 24,
          }}>
          <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: "90vw", maxHeight: "84vh" }}>
            <img src={ixnImages[fullscreenIdx].url} alt={ixnImages[fullscreenIdx].name}
              style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 12, display: "block", boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }} />
            <button onClick={() => setFullscreenIdx(null)}
              style={{ position: "absolute", top: -13, right: -13, width: 28, height: 28, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
              <X size={13} />
            </button>
            {fullscreenIdx > 0 && (
              <button onClick={e => { e.stopPropagation(); setFullscreenIdx(i => i - 1); }}
                style={{ position: "absolute", top: "50%", left: -46, transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronLeft size={18} />
              </button>
            )}
            {fullscreenIdx < ixnImages.length - 1 && (
              <button onClick={e => { e.stopPropagation(); setFullscreenIdx(i => i + 1); }}
                style={{ position: "absolute", top: "50%", right: -46, transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronRight size={18} />
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              {ixnImages[fullscreenIdx].name} · {fullscreenIdx + 1} / {ixnImages.length}
            </span>
            <button onClick={e => { e.stopPropagation(); downloadSingleImg(fullscreenIdx);  }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.3)", background: "#735AA7", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              <Download size={12} /> Download
            </button>
          </div>
        </div>
      )}
    </>
  );
}